/**
 * Expected value, variance and standard deviation of a discrete random variable.
 *
 *   E(X) = Σ x·P(x),  Var(X) = Σ (x − μ)²·P(x),  SD(X) = √Var(X).
 *
 * The second column is either a probability (decimal, percentage or fraction) or a
 * frequency, which is divided by the total to give the empirical probability.
 */

import {
  LINE_BREAK,
  lineTokens,
  parseFlexibleNumber,
} from "@/lib/tool-kit/stats-descriptive/inputs";

export type WeightKind = "probability" | "frequency";

/** Probabilities this close to summing to 1 are treated as rounded, not wrong. */
export const PROBABILITY_TOLERANCE = 0.01;

export interface OutcomeLine {
  x: number;
  weight: number;
}

export interface ParsedOutcomes {
  rows: OutcomeLine[];
  /** 1-based numbers of non-blank lines that did not hold a value and a weight. */
  invalidLines: number[];
}

/**
 * One outcome per line: the value, then its probability or frequency. Any leading text
 * ("Win 3 1/6") is skipped, so a label can sit in front of the numbers.
 */
export function parseOutcomes(text: string): ParsedOutcomes {
  const rows: OutcomeLine[] = [];
  const invalidLines: number[] = [];
  const lines = text.split(LINE_BREAK);
  for (const [index, line] of lines.entries()) {
    if (line.trim() === "") {
      continue;
    }
    const numbers = lineTokens(line)
      .map(parseFlexibleNumber)
      .filter((value): value is number => value !== null);
    if (numbers.length >= 2) {
      rows.push({ x: numbers[0], weight: numbers[1] });
    } else {
      invalidLines.push(index + 1);
    }
  }
  return { rows, invalidLines };
}

export interface ExpectedValueRow {
  x: number;
  p: number;
  /** x · P(x). */
  xp: number;
  /** (x − μ)² · P(x). */
  squaredDeviationP: number;
}

export type DistributionError =
  | { error: "negative-weight" }
  | { error: "probability-above-one" }
  | { error: "zero-total" }
  | { error: "probabilities-do-not-sum"; sum: number };

export interface ExpectedValueReport {
  rows: ExpectedValueRow[];
  kind: WeightKind;
  /** Σ P(x) as entered (probabilities), or 1 (frequencies). */
  sumP: number;
  /** Σ f, for frequencies. */
  totalFrequency: number | null;
  mean: number;
  variance: number;
  sd: number;
  /** Σ P(x) is within tolerance of 1 but not equal to it. */
  roundedSum: boolean;
}

function validate(
  rows: readonly OutcomeLine[],
  kind: WeightKind
): DistributionError | null {
  if (rows.some((row) => row.weight < 0)) {
    return { error: "negative-weight" };
  }
  if (kind === "probability" && rows.some((row) => row.weight > 1)) {
    return { error: "probability-above-one" };
  }
  let sum = 0;
  for (const row of rows) {
    sum += row.weight;
  }
  if (!(sum > 0)) {
    return { error: "zero-total" };
  }
  // The epsilon keeps 0.33 + 0.33 + 0.33 = 0.9900000000000001 inside the tolerance.
  if (
    kind === "probability" &&
    Math.abs(sum - 1) > PROBABILITY_TOLERANCE + 1e-9
  ) {
    return { error: "probabilities-do-not-sum", sum };
  }
  return null;
}

export function expectedValue(
  outcomes: readonly OutcomeLine[],
  kind: WeightKind
): ExpectedValueReport | DistributionError | null {
  if (outcomes.length === 0) {
    return null;
  }
  const problem = validate(outcomes, kind);
  if (problem) {
    return problem;
  }
  let total = 0;
  for (const row of outcomes) {
    total += row.weight;
  }
  const probabilities = outcomes.map((row) =>
    kind === "frequency" ? row.weight / total : row.weight
  );
  let mean = 0;
  for (const [index, row] of outcomes.entries()) {
    mean += row.x * probabilities[index];
  }
  const rows = outcomes.map((row, index) => {
    const p = probabilities[index];
    return {
      x: row.x,
      p,
      xp: row.x * p,
      squaredDeviationP: (row.x - mean) ** 2 * p,
    };
  });
  let variance = 0;
  for (const row of rows) {
    variance += row.squaredDeviationP;
  }
  const sumP = kind === "frequency" ? 1 : total;
  return {
    rows,
    kind,
    sumP,
    totalFrequency: kind === "frequency" ? total : null,
    mean,
    variance,
    sd: Math.sqrt(variance),
    roundedSum: kind === "probability" && Math.abs(total - 1) > 1e-9,
  };
}

export function isDistributionError(
  result: ExpectedValueReport | DistributionError
): result is DistributionError {
  return "error" in result;
}
